import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Product", target: "features" },
  { label: "Manifesto", target: "manifesto" },
  { label: "About", target: "about" },
  { label: "Contact", target: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lenis = useLenis();
  const { user } = useAuth();

  const initial = (user?.name || user?.email || user?.phone || "?").trim().charAt(0).toUpperCase();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToId = (target) => {
    if (lenis) {
      lenis.scrollTo(`#${target}`, { offset: -80, duration: 1.1 });
    } else {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goTo = (target) => {
    setOpen(false);
    if (location.pathname !== "/home") {
      navigate("/home", { state: { scrollTo: target } });
      return;
    }
    scrollToId(target);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-paper/80 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[76px] flex items-center justify-between">
        <Link to="/home" data-testid="navbar-logo" onClick={() => window.scrollTo({ top: 0 })}>
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.target}
              onClick={() => goTo(l.target)}
              data-testid={`nav-${l.target}`}
              className="text-sm font-medium text-ink-soft hover:text-ink transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => { setOpen(false); navigate("/pro"); }}
            data-testid="nav-pro"
            className="text-sm font-medium text-ink-soft hover:text-ink transition-colors"
          >
            StayFlow Pro
          </button>
          <button
            onClick={() => navigate("/stays")}
            data-testid="nav-stays"
            className="text-sm font-medium text-ink-soft hover:text-ink transition-colors"
          >
            Book a Stay
          </button>
          {user ? (
            <button
              onClick={() => navigate("/profile")}
              data-testid="nav-account"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-soft hover:text-ink transition-colors"
              title="Your account"
            >
              {user.picture ? (
                <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover ring-1 ring-black/10" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-brand text-white text-sm font-bold flex items-center justify-center">{initial}</span>
              )}
              <span className="hidden lg:inline max-w-[110px] truncate">{user.name || "Account"}</span>
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="nav-signin"
              className="text-sm font-medium text-ink-soft hover:text-ink transition-colors"
            >
              Sign in
            </button>
          )}
          <button
            onClick={() => goTo("download")}
            data-testid="nav-download-cta"
            className="group inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-medium pl-5 pr-4 py-2.5 transition-transform duration-200 hover:-translate-y-0.5"
          >
            Get the app
            <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          className="md:hidden p-2 -mr-2 text-ink"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-paper border-b border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.target}
                  onClick={() => goTo(l.target)}
                  className="text-left py-3 text-lg font-heading font-medium text-ink"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); navigate("/pro"); }}
                data-testid="nav-pro-mobile"
                className="text-left py-3 text-lg font-heading font-medium text-ink"
              >
                StayFlow Pro
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/stays"); }}
                data-testid="nav-stays-mobile"
                className="text-left py-3 text-lg font-heading font-medium text-ink"
              >
                Book a Stay
              </button>
              <button
                onClick={() => { setOpen(false); navigate(user ? "/profile" : "/login"); }}
                data-testid="nav-signin-mobile"
                className="text-left py-3 text-lg font-heading font-medium text-ink"
              >
                {user ? (user.name ? `Hi, ${user.name.split(" ")[0]}` : "Your account") : "Sign in"}
              </button>
              <button
                onClick={() => goTo("download")}
                className="mt-3 inline-flex items-center justify-center rounded-full bg-brand text-white font-medium py-3"
              >
                Get the app
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
